import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { Tile, TileStripe } from "@dash/tile";
import { css, useTheme } from "@emotion/react";
import { useState } from "react";

import { Order, PackageItem } from "@megaron/fulfillment-contracts";

import { OrderPackingStatusBanners } from "./OrderPackingStatusBanners";

type Props = {
  order: Order;
  items: PackageItem[];
  isPackingLocked?: boolean;
  getTotalOrderQuantity: (variantId: string) => number;
  activeId: number | null;
  onAddItem: (item: PackageItem) => void;
  getDisplayName: (variantId: string, fallbackName: string) => string;
};

export const ToPackList = ({
  order,
  items,
  isPackingLocked = false,
  getTotalOrderQuantity,
  activeId,
  onAddItem,
  getDisplayName,
}: Props) => {
  const theme = useTheme();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleAdd = (item: PackageItem) => {
    onAddItem({ ...item, quantity: 1 });
    setDialogOpen(false);
  };

  const hasAvailableItems = items.some((i) => i.quantity > 0);

  const sortedItems = [...items].sort((a, b) => {
    const totalA = getTotalOrderQuantity(a.variantId);
    const totalB = getTotalOrderQuantity(b.variantId);
    const packedA = totalA - a.quantity;
    const packedB = totalB - b.quantity;
    const isCompleteA = packedA === totalA;
    const isCompleteB = packedB === totalB;
    if (isCompleteA !== isCompleteB) return Number(isCompleteA) - Number(isCompleteB);
    if (!isCompleteA && !isCompleteB) return Number(b.weight ?? 0) - Number(a.weight ?? 0);
    return 0;
  });

  if (order.cancelledAt != null) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <OrderPackingStatusBanners order={order} />
      </div>
    );
  }

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <OrderPackingStatusBanners order={order} />
      <SectionHeader isHr>Do zapakowania</SectionHeader>

      {sortedItems.map((item) => {
        const total = getTotalOrderQuantity(item.variantId);
        const packed = total - item.quantity;
        const isComplete = packed === total;

        return (
          <Tile
            key={item.variantId}
            gap="8px"
            stripe={<TileStripe color={isComplete ? theme.colors.primaryLight : theme.colors.primary} />}
          >
            <div
              css={css({
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              })}
            >
              <div
                css={css({
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "1rem",
                  fontWeight: 600,
                })}
              >
                <span>{getDisplayName(item.variantId, item.name)}</span>
                <span>
                  {packed} / {total} szt.
                </span>
              </div>
              {item.engraving && (
                <div css={{ fontSize: "0.85rem", color: theme.colors.default[600] }}>
                  Grawerunek: &quot;{item.engraving}&quot;
                </div>
              )}
            </div>
          </Tile>
        );
      })}

      {!isPackingLocked && (
        <Button
          variant="outline"
          size="small"
          css={{ marginLeft: "auto" }}
          onClick={() => setDialogOpen(true)}
          isDisabled={activeId === null || !hasAvailableItems}
        >
          Dodaj produkt
        </Button>
      )}

      {isDialogOpen && (
        <Dialog header="Dodaj produkt do paczki" onClose={() => setDialogOpen(false)} css={{ maxWidth: 480 }}>
          <div css={{ display: "flex", flexDirection: "column", gap: "12px", padding: "1rem 0" }}>
            {items
              .filter((i) => i.quantity > 0)
              .sort((a, b) => Number(b.weight ?? 0) - Number(a.weight ?? 0))
              .map((item) => (
                <Tile
                  key={item.variantId}
                  gap="8px"
                  stripe={<TileStripe color={theme.colors.primary} />}
                  css={{ cursor: "pointer" }}
                  onClick={() => handleAdd(item)}
                >
                  <div css={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{getDisplayName(item.variantId, item.name)}</span>
                      <span>{item.quantity} szt.</span>
                    </div>
                    {item.engraving && (
                      <div css={{ fontSize: "0.85rem", color: theme.colors.default[600] }}>
                        Grawerunek: &quot;{item.engraving}&quot;
                      </div>
                    )}
                  </div>
                </Tile>
              ))}
          </div>
        </Dialog>
      )}
    </div>
  );
};
